export type LogicalOperator = 'AND' | 'OR';

export type FieldType = 'member.type' | 'member.customer_id';

export type Condition = {
  id: string;
  field: FieldType;
  operator: 'Equals';
  value: string;
};

export type RuleGroup = {
  id: string;
  logic: LogicalOperator;
  rules: RuleOrGroup[];
};
export type RuleOrGroup = Condition | RuleGroup;

export const generateElementUId = (): string => Math.random().toString(36);

export const fieldOptions: { label: string; value: FieldType }[] = [
  { label: 'member.type', value: 'member.type' },
  { label: 'member.customer_id', value: 'member.customer_id' },
];

export const valueOptionsMap: Record<
  FieldType,
  { label: string; value: string }[]
> = {
  'member.type': [
    { label: '1 - User', value: '1' },
    { label: '2 - Service Account', value: '2' },
    { label: '3 - Group', value: '3' },
  ],
  'member.customer_id': [{ label: 'CUSTOMERID', value: 'groupCustomerId()' }],
};

export const isCondition = (item: RuleOrGroup): item is Condition =>
  'field' in item;

export const isRuleGroup = (item: RuleOrGroup): item is RuleGroup =>
  'logic' in item && Array.isArray((item as RuleGroup).rules);

export const createDefaultCondition = (): Condition => ({
  id: generateElementUId(),
  field: 'member.type',
  operator: 'Equals',
  value: '1',
});

export const createConditionBlock = (): RuleGroup => ({
  id: generateElementUId(),
  logic: 'AND',
  rules: [createDefaultCondition()],
});

const flattenInnerBlock = (group: RuleGroup): RuleGroup => ({
  ...group,
  rules: group.rules.flatMap((item) =>
    isCondition(item) ? [item] : (item as RuleGroup).rules.filter(isCondition)
  ),
});

export const normalizeRuleGroupToBlocks = (group: RuleGroup): RuleGroup => {
  if (!group.rules || group.rules.length === 0) {
    return { ...group, rules: [] };
  }

  if (group.rules.every(isCondition)) {
    return {
      ...group,
      rules: [
        {
          id: generateElementUId(),
          logic: group.logic,
          rules: group.rules,
        },
      ],
    };
  }

  return {
    ...group,
    rules: group.rules.map((item) => {
      if (isCondition(item)) {
        return {
          id: generateElementUId(),
          logic: 'AND' as LogicalOperator,
          rules: [item],
        };
      }
      return flattenInnerBlock(item);
    }),
  };
};

export const parseQuery = (query: string): RuleGroup => {
  const trim = (s: string) => s.trim();

  function findMatchingParen(str: string, startIndex: number): number {
    let depth = 1;
    for (let i = startIndex + 1; i < str.length; i++) {
      if (str[i] === '(') depth++;
      else if (str[i] === ')') {
        depth--;
        if (depth === 0) return i;
      }
    }
    return -1;
  }

  function splitByTopLevelOperator(
    expression: string,
    operator: string
  ): string[] | null {
    const parts: string[] = [];
    let partStartIndex = 0;
    let depth = 0;
    let operatorFoundForSplitting = false;

    for (let i = 0; i < expression.length; i++) {
      if (expression[i] === '(') depth++;
      else if (expression[i] === ')') depth--;
      else if (depth === 0 && expression.substring(i).startsWith(operator)) {
        parts.push(trim(expression.substring(partStartIndex, i)));
        partStartIndex = i + operator.length;
        i += operator.length - 1;
        operatorFoundForSplitting = true;
      }
    }
    parts.push(trim(expression.substring(partStartIndex)));

    const nonEmptyParts = parts.filter((p) => p.length > 0);
    return operatorFoundForSplitting && nonEmptyParts.length > 0
      ? nonEmptyParts
      : null;
  }

  function parseSubExpression(subQuery: string): RuleOrGroup | null {
    let currentQuery = trim(subQuery);
    if (currentQuery === '') return null;

    while (currentQuery.startsWith('(') && currentQuery.endsWith(')')) {
      const matchingParenIndex = findMatchingParen(currentQuery, 0);
      if (matchingParenIndex === currentQuery.length - 1) {
        currentQuery = trim(currentQuery.substring(1, currentQuery.length - 1));
        if (currentQuery === '') return null;
      } else {
        break;
      }
    }
    if (currentQuery === '') return null;

    const orSegments = splitByTopLevelOperator(currentQuery, ' || ');
    if (orSegments) {
      const rules = orSegments
        .map(parseSubExpression)
        .filter(Boolean) as RuleOrGroup[];
      if (rules.length === 0) return null;
      return rules.length === 1
        ? rules[0]
        : { id: generateElementUId(), logic: 'OR', rules };
    }

    const andSegments = splitByTopLevelOperator(currentQuery, ' && ');
    if (andSegments) {
      const rules = andSegments
        .map(parseSubExpression)
        .filter(Boolean) as RuleOrGroup[];
      if (rules.length === 0) return null;
      return rules.length === 1
        ? rules[0]
        : { id: generateElementUId(), logic: 'AND', rules };
    }

    const conditionMatch = currentQuery.match(/^(.+?)\s*==\s*(.+)$/);
    if (conditionMatch) {
      let fieldNameInput = trim(conditionMatch[1]);
      const value = trim(conditionMatch[2]);

      if (fieldNameInput.toLowerCase() === 'member.customer_id') {
        fieldNameInput = 'member.customer_id';
      }

      const matchedOption = fieldOptions.find(
        (opt) => opt.value.toLowerCase() === fieldNameInput.toLowerCase()
      );
      if (matchedOption) {
        return {
          id: generateElementUId(),
          field: matchedOption.value,
          operator: 'Equals',
          value,
        };
      }
      return null;
    }
    return null;
  }

  const parsedResult = parseSubExpression(query);

  if (!parsedResult) {
    return { id: generateElementUId(), logic: 'AND', rules: [] };
  }

  if (isCondition(parsedResult)) {
    return normalizeRuleGroupToBlocks({
      id: generateElementUId(),
      logic: 'AND',
      rules: [parsedResult],
    });
  }

  return normalizeRuleGroupToBlocks(parsedResult);
};

export const groupQueryString = (group: RuleGroup): string => {
  if (!group || !group.rules || group.rules.length === 0) {
    return '';
  }
  return group.rules
    .map((item) => {
      if (isRuleGroup(item)) {
        const nestedQuery = groupQueryString(item);
        return nestedQuery ? `(${nestedQuery})` : '';
      } else {
        const condition = item as Condition;
        if (condition.field && typeof condition.value !== 'undefined') {
          return `${condition.field} == ${condition.value}`;
        }
        return '';
      }
    })
    .filter((str) => str !== '')
    .join(group.logic === 'AND' ? ' && ' : ' || ');
};

const MEMBER_TYPE_LABELS: Record<string, string> = {
  '1': 'Users',
  '2': 'Service Accounts',
  '3': 'Groups',
};

const describeCondition = (condition: Condition): string => {
  if (condition.field === 'member.type') {
    return MEMBER_TYPE_LABELS[condition.value] ?? condition.value;
  }
  if (condition.field === 'member.customer_id') {
    return 'this organization';
  }
  return `${condition.field} == ${condition.value}`;
};

export const describeQuery = (group: RuleGroup): string => {
  if (!group || !group.rules || group.rules.length === 0) {
    return '';
  }
  const needsGrouping = group.rules.length > 1;
  const parts = group.rules
    .map((item) => {
      if (!isRuleGroup(item)) {
        return describeCondition(item);
      }
      const inner = describeQuery(item);
      return needsGrouping ? `(${inner})` : inner;
    })
    .filter((part) => part !== '');
  return parts.join(group.logic === 'AND' ? ' and ' : ' or ');
};
